import {useCallback, useEffect, useMemo, useState} from 'react';
import {Box, Button, Skeleton, Stack, Typography} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import {SplitTabsPanel} from '@/components/common';
import {
    AuditProgramSectionVO,
    getASectionVO,
    saveASectionPage,
} from '@/services/auditService/auditProgramService';
import {
    A1_CONTENT,
    A12Row,
    A13_FOOTER,
    A13Row,
    A_PAGE_TITLES,
    A_SECTION_INDEX,
    A_SECTION_INDEX_LETTER,
    A_SECTION_INDEX_TITLE,
    A_SECTION_PAGES,
    ASectionPageCode,
    mergeA1HeaderFromApi,
    syncA12DatesFromA1PartnerDate,
    syncA13DatesFromA1PartnerDate,
    withA12Defaults,
    withA13Defaults,
} from '@/pages/components/audit-program/a-section/constants';
import A1MemorandumView from '@/pages/components/audit-program/a-section/A1MemorandumView';
import A12SubsequentEventsView from '@/pages/components/audit-program/a-section/A12SubsequentEventsView';
import A13ChecklistView from '@/pages/components/audit-program/a-section/A13ChecklistView';
import AiIndexView from '@/pages/components/audit-program/a-section/AiIndexView';
import type {DeriveActiveIndexResult} from '@/pages/components/audit-program/ai-section/deriveActiveIndex';
import PageTabLabel from '@/pages/components/audit-program/PageTabLabel';
import {useProgramSectionTemplates} from '@/pages/components/audit-program/templates/useProgramSectionTemplates';

export type ASectionViewProps = {
    companyId: number;
    annualId: number;
    companyName?: string | null;
    /** Open a specific A-section page (e.g. A1.2 from directory T). */
    initialPage?: ASectionPageCode;
    /** BS/PL-driven active index letters — Ai page strikethrough for inactive sections. */
    activeIndex?: DeriveActiveIndexResult | null;
};

function toStringRecord(data?: Record<string, unknown>): Record<string, string> {
    if (!data) return {};
    return Object.fromEntries(
        Object.entries(data).map(([k, v]) => [k, v == null ? '' : String(v)]),
    );
}

export default function ASectionView({
    companyId,
    annualId,
    companyName,
    initialPage = 'Ai',
    activeIndex = null,
}: ASectionViewProps) {
    const [pageKey, setPageKey] = useState<ASectionPageCode>(initialPage);
    const [header, setHeader] = useState<AuditProgramSectionVO | null>(null);
    const [pageData, setPageData] = useState<Record<string, Record<string, string>>>({});
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [dirty, setDirty] = useState<Record<string, boolean>>({});
    const {getContent} = useProgramSectionTemplates('A');

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const data = await getASectionVO(companyId, annualId);
            if (companyName && !data.client) {
                data.client = companyName;
            }
            setHeader(data);
            const pages: Record<string, Record<string, string>> = {};
            for (const code of A_SECTION_PAGES) {
                pages[code] = toStringRecord(data.pages?.[code]);
            }
            pages.A1 = mergeA1HeaderFromApi(pages.A1 ?? {}, data);
            pages['A1.2'] = withA12Defaults(pages['A1.2'] ?? {}, {
                header: data,
                enquiryDirectorName: data.enquiryDirectorName,
                a1PartnerDate: pages.A1?.partnerDate,
            });
            pages['A1.3'] = withA13Defaults(pages['A1.3'] ?? {}, {
                header: data,
                a1PartnerDate: pages.A1?.partnerDate,
            });
            setPageData(pages);
            setDirty({});
        } catch (e: unknown) {
            const msg = e instanceof Error ? e.message : 'Failed to load A Section';
            setError(msg);
            setHeader(null);
        } finally {
            setLoading(false);
        }
    }, [companyId, annualId, companyName]);

    useEffect(() => {
        void load();
    }, [load]);

    useEffect(() => {
        setPageKey(initialPage);
    }, [initialPage]);

    const handleFieldChange = useCallback((pageCode: ASectionPageCode, key: string, value: string) => {
        setPageData((prev) => {
            const nextPage = {...(prev[pageCode] ?? {}), [key]: value};
            if (pageCode === 'A1' && key === 'partnerDate') {
                const prevPartnerDate = prev.A1?.partnerDate;
                return {
                    ...prev,
                    A1: nextPage,
                    'A1.2': syncA12DatesFromA1PartnerDate(prev['A1.2'] ?? {}, value, prevPartnerDate),
                    'A1.3': syncA13DatesFromA1PartnerDate(prev['A1.3'] ?? {}, value, prevPartnerDate),
                };
            }
            return {...prev, [pageCode]: nextPage};
        });
        setDirty((prev) => {
            if (pageCode === 'A1' && key === 'partnerDate') {
                return {...prev, A1: true, 'A1.2': true, 'A1.3': true};
            }
            return {...prev, [pageCode]: true};
        });
    }, []);

    const handleSave = useCallback(async () => {
        if (pageKey === 'Ai' || !dirty[pageKey]) return;
        setSaving(true);
        setError(null);
        try {
            await saveASectionPage(companyId, annualId, pageKey, pageData[pageKey] ?? {});
            setDirty((prev) => ({...prev, [pageKey]: false}));
        } catch (e: unknown) {
            const msg = e instanceof Error ? e.message : 'Failed to save';
            setError(msg);
        } finally {
            setSaving(false);
        }
    }, [companyId, annualId, dirty, pageKey, pageData]);

    const a1Content = (getContent('A1') ?? A1_CONTENT) as typeof A1_CONTENT;
    const a12Content = getContent('A1.2') as {title?: string; rows?: A12Row[]} | undefined;
    const a13Content = getContent('A1.3') as {
        title?: string;
        rows?: A13Row[];
        footer?: typeof A13_FOOTER;
    } | undefined;

    const tabs = useMemo(() => A_SECTION_PAGES.map((code) => ({
        key: code,
        label: <PageTabLabel code={code} title={A_PAGE_TITLES[code]}/>,
        icon: code,
        content: header ? (
            code === 'Ai' ? (
                <AiIndexView
                    header={header}
                    sectionLetter={A_SECTION_INDEX_LETTER}
                    sectionTitle={A_SECTION_INDEX_TITLE}
                    sectionRows={A_SECTION_INDEX}
                    activeIndex={activeIndex}
                    onNavigate={(pageCode) => setPageKey(pageCode as ASectionPageCode)}
                />
            ) : code === 'A1' ? (
                <A1MemorandumView
                    header={header}
                    values={pageData.A1 ?? {}}
                    onChange={(k, v) => handleFieldChange('A1', k, v)}
                    content={a1Content}
                />
            ) : code === 'A1.2' ? (
                <A12SubsequentEventsView
                    header={header}
                    values={pageData['A1.2'] ?? {}}
                    onChange={(k, v) => handleFieldChange('A1.2', k, v)}
                    title={a12Content?.title}
                    rows={a12Content?.rows}
                />
            ) : (
                <A13ChecklistView
                    header={header}
                    values={pageData['A1.3'] ?? {}}
                    onChange={(k, v) => handleFieldChange('A1.3', k, v)}
                    title={a13Content?.title}
                    rows={a13Content?.rows}
                    footer={a13Content?.footer}
                />
            )
        ) : null,
    })), [header, pageData, handleFieldChange, a1Content, a12Content, a13Content, activeIndex]);

    if (loading) {
        return <Skeleton variant="rectangular" height={480} sx={{m: 2}}/>;
    }

    if (error && !header) {
        return (
            <Box sx={{p: 2}}>
                <Typography color="error">{error}</Typography>
            </Box>
        );
    }

    return (
        <Box sx={{height: '100%', minHeight: 0, display: 'flex', flexDirection: 'column'}}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{px: 2, pt: 1, pb: 0.5}}>
                <Typography variant="subtitle1" fontWeight={600}>
                    A Section — Final Completion
                </Typography>
                <Button
                    variant="contained"
                    size="small"
                    startIcon={<SaveIcon/>}
                    disabled={pageKey === 'Ai' || !dirty[pageKey] || saving}
                    onClick={() => void handleSave()}
                >
                    {pageKey === 'Ai' ? 'Display only' : `Save ${pageKey}`}
                </Button>
            </Stack>
            {error ? (
                <Typography color="error" sx={{px: 2, pb: 1}} variant="body2">{error}</Typography>
            ) : null}
            <Box sx={{flex: 1, minHeight: 0}}>
                <SplitTabsPanel
                    tabs={tabs}
                    selectedKey={pageKey}
                    onChange={(k) => setPageKey(k as ASectionPageCode)}
                    height="100%"
                    allowMaximize={false}
                    leftPaneSx={{minWidth: 180}}
                    contentWrapperSx={{height: '100%', overflow: 'auto', p: 0}}
                    rightPaneSx={{height: '100%', overflow: 'hidden'}}
                />
            </Box>
        </Box>
    );
}
